import { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Bot, User, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../lib/auth';
import api from '../lib/api';

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const pollingRef = useRef(null);
  const lastMessageCountRef = useRef(0);
  const userScrolledUpRef = useRef(false);
  const { user, isAuthenticated } = useAuth();

  // Scroll to bottom - only when explicitly requested
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Handle scroll event to detect if user manually scrolled up
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    // Check if user is NOT at the bottom (scrolled up)
    const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 50;
    userScrolledUpRef.current = !isAtBottom;
  }, []);

  // Scroll to bottom only for user's own messages
  useEffect(() => {
    if (messages.length > lastMessageCountRef.current) {
      const lastMessage = messages[messages.length - 1];
      // Only auto-scroll if user sent a message themselves
      if (lastMessage?.role === 'customer') {
        userScrolledUpRef.current = false;
        scrollToBottom();
      }
      lastMessageCountRef.current = messages.length;
    }
  }, [messages, scrollToBottom]);

  // Load chat history
  const loadChatHistory = useCallback(async (sid) => {
    if (!sid) return;
    
    try {
      // Use different endpoint for guests vs authenticated users
      const endpoint = isAuthenticated ? `/chat/history/${sid}` : `/chat/guest/history/${sid}`;
      const response = await api.get(endpoint);
      if (response.data?.messages) {
        const newMessages = response.data.messages.map(msg => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp,
          admin_name: msg.admin_name
        }));
        
        // Check if there are new messages
        if (newMessages.length > messages.length) {
          const lastMsg = newMessages[newMessages.length - 1];
          if (lastMsg.role === 'admin' && !isOpen) {
            setHasNewMessage(true);
          }
        }
        
        setMessages(newMessages);
      }
    } catch (err) {
      // Session might not exist or user not authorized
      console.log('Could not load chat history');
    }
  }, [messages.length, isOpen, isAuthenticated]);

  // Load existing session from localStorage on mount
  useEffect(() => {
    const savedSessionId = localStorage.getItem('farbwelt_chat_session');
    if (savedSessionId) {
      setSessionId(savedSessionId);
    }
  }, []);

  // Poll for new messages when chat is open and we have a session
  useEffect(() => {
    if (sessionId && isOpen) {
      // Load immediately
      loadChatHistory(sessionId);
      
      // Poll every 3 seconds for new messages (to get admin replies)
      pollingRef.current = setInterval(() => {
        loadChatHistory(sessionId);
      }, 3000);
    }
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [sessionId, isOpen, loadChatHistory]);

  // Clear new message indicator when opening chat
  useEffect(() => {
    if (isOpen) {
      setHasNewMessage(false);
      userScrolledUpRef.current = false;
      // Scroll to bottom when opening chat
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [isOpen, scrollToBottom]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    setError(null);
    
    // Add user message to UI immediately
    setMessages(prev => [...prev, {
      role: 'customer',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);
    
    setIsLoading(true);
    
    try {
      // Choose endpoint based on auth status
      const endpoint = isAuthenticated ? '/chat/message' : '/chat/guest/message';
      
      const response = await api.post(endpoint, {
        message: userMessage,
        session_id: sessionId
      });
      
      // Save session ID
      if (response.data.session_id) {
        setSessionId(response.data.session_id);
        localStorage.setItem('farbwelt_chat_session', response.data.session_id);
      }
      
      // Add AI response to messages
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      }]);
      
    } catch (err) {
      console.error('Chat error:', err);
      // More specific error handling
      let errorMessage = 'Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.';
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Die Antwort dauert zu lange. Bitte versuchen Sie es erneut.';
      } else if (err.response?.status === 429) {
        errorMessage = 'Zu viele Anfragen. Bitte warten Sie einen Moment.';
      } else if (!navigator.onLine) {
        errorMessage = 'Keine Internetverbindung. Bitte prüfen Sie Ihre Verbindung.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
    localStorage.removeItem('farbwelt_chat_session');
    setMessages([{
      role: 'assistant',
      content: 'Hallo! Ich bin der FarbWelt Kundenservice. Wie kann ich Ihnen helfen?',
      timestamp: new Date().toISOString()
    }]);
  };

  // Initialize with welcome message when opened for first time
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: 'Hallo! Ich bin der FarbWelt Kundenservice. Wie kann ich Ihnen helfen?',
        timestamp: new Date().toISOString()
      }]);
    }
  }, [isOpen, messages.length]);

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-r from-[#9B1B30] to-[#7B1526] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
        data-testid="chat-toggle-btn"
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <>
            <MessageCircle className="w-6 h-6" />
            {hasNewMessage ? (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-[10px] font-bold">!</span>
              </span>
            ) : (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
            )}
          </>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-24 right-4 md:right-6 z-50 w-[calc(100vw-32px)] md:w-[380px] max-w-[400px] h-[70vh] md:h-[500px] max-h-[calc(100vh-150px)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
          data-testid="chat-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#9B1B30] to-[#7B1526] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Kundenservice</h3>
                <p className="text-xs text-white/80">Wir helfen Ihnen gerne</p>
              </div>
            </div>
            <button
              onClick={startNewChat}
              className="text-xs bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full transition-colors"
              data-testid="new-chat-btn"
            >
              Neuer Chat
            </button>
          </div>

          {/* Messages Area */}
          <div 
            ref={messagesContainerRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50"
          >
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'customer' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    msg.role === 'customer'
                      ? 'bg-gradient-to-r from-[#9B1B30] to-[#7B1526] text-white rounded-br-md'
                      : msg.role === 'admin'
                      ? 'bg-blue-600 text-white rounded-bl-md'
                      : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
                  }`}
                >
                  {msg.role === 'admin' && (
                    <p className="text-xs text-blue-200 mb-1 font-medium">
                      {msg.admin_name || 'Kundenservice'}
                    </p>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  <p className={`text-xs mt-1 ${
                    msg.role === 'customer' || msg.role === 'admin' ? 'text-white/60' : 'text-gray-400'
                  }`}>
                    {new Date(msg.timestamp).toLocaleTimeString('de-DE', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-[#9B1B30]" />
                    <span className="text-sm text-gray-500">Schreibe...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Error Message */}
          {error && (
            <div className="px-4 py-2 bg-red-50 border-t border-red-100 flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {/* Input Area */}
          <div className="p-4 bg-white border-t border-gray-100">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Schreiben Sie Ihre Nachricht..."
                className="flex-1 px-4 py-2.5 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1B30]/50 transition-all"
                disabled={isLoading}
                data-testid="chat-input"
              />
              <button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="w-10 h-10 bg-gradient-to-r from-[#9B1B30] to-[#7B1526] text-white rounded-full flex items-center justify-center hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            {!isAuthenticated && (
              <p className="text-xs text-gray-400 mt-2 text-center">
                Für personalisierte Hilfe bitte <a href="/login" className="text-[#9B1B30] hover:underline">anmelden</a>
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
